import { useEffect, useState } from "react";
import useIsMobile from "../../../../hooks/useIsMobile";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "primereact/skeleton";

const RemainingPackageDetailList = (props) => {
  const { data, filterValue, loading, product_name } = props;
  const [viewType, setViewType] = useState("card");
  const viewOptions = [
    { label: "Card View", value: "card" },
    { label: "Table View", value: "table" },
  ];
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const isMobile = useIsMobile(750);

  useEffect(() => {
    if (isMobile) {
      setViewType("card");
    } else {
      setViewType("table");
    }
  }, [isMobile]);

  const formatPax = (value) => `${value} pax`;

  // Body templates for card and table views
  const CustomerMobileTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const rank = rowIndex + 1;
    return (
      <div className="row-start-center w-full gap-4">
        <div className="col-center-start w-full">
          <div className="row-between-center gap-2 w-full">
            <div className="text-gray-800 font-semibold text-sm sm:text-base row-start-center gap-1">
              <span className="flex items-center justify-center text-sm font-bold text-purple-500">
                {rank}.
              </span>
              {rowData.customer_name}
            </div>
            <div
              className={`text-white text-center text-xs sm:text-sm font-medium bg-gradient-to-br rounded-md min-w-[3rem] w-[3rem] sm:w-[4rem] py-1 ${
                rowData.level_name === "Gold"
                  ? "from-yellow-400 to-orange-500"
                  : rowData.level_name === "Silver"
                  ? "from-gray-400 to-gray-950"
                  : "from-green-500 to-emerald-950"
              }`}
            >
              {rowData.level_name}
            </div>
          </div>
          <span className="text-gray-600 text-xs">{rowData.customer_code}</span>
          <div className="w-full col-center-start text-xs sm:text-md text-gray-800 font-semibold mt-1">
            <span>BC: {rowData.bc_name}</span>
            <span>Quantity: {formatPax(rowData.quantity)}</span>
          </div>
        </div>
      </div>
    );
  };

  const customerBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const rank = rowIndex + 1;
    return (
      <div className="row-start-center w-full gap-4">
        <span className="min-w-[3rem] min-h-[3rem] w-[3rem] h-[3rem] flex items-center justify-center text-md font-bold text-purple-500">
          {rank}.
        </span>
        <div className="col-center-start">
          <span className="text-gray-800 font-semibold text-base">
            {rowData.customer_name}
          </span>
          <span className="text-gray-600 text-sm">{rowData.customer_code}</span>
          <div
            className={`text-white text-center text-xs font-medium bg-gradient-to-br rounded-md px-4 py-1 ${
              rowData.level_name === "Gold"
                ? "from-yellow-400 to-orange-500"
                : rowData.level_name === "Silver"
                ? "from-gray-400 to-gray-950"
                : "from-green-500 to-emerald-950"
            }`}
          >
            {rowData.level_name}
          </div>
        </div>
      </div>
    );
  };

  const BCBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">{rowData.bc_name}</div>
    );
  };

  const packageBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">
        {formatPax(rowData.quantity)}
      </div>
    );
  };

  // Skeleton for table rows
  const TableSkeleton = ({ rows = 10 }) => {
    const skeletonRows = Array.from({ length: rows }, (_, i) => ({ id: i }));

    const mobileCardNameSkeleton = () => (
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-1 w-full">
          <div className="row-between-center w-full">
            <div className="row-start-center gap-2">
              <Skeleton width="1.25rem" height="1.25rem" />
              <Skeleton width="6rem" height="1rem" />
            </div>
            <Skeleton width="3rem" height="1.5rem" />
          </div>
          <Skeleton width="3rem" height="0.8rem" />
          <Skeleton width="8rem" height="1rem" />
          <Skeleton width="8rem" height="1rem" />
        </div>
      </div>
    );

    const nameSkeleton = () => (
      <div className="row-start-center gap-2">
        <Skeleton width="2rem" height="2rem" className="flex-shrink-0" />
        <Skeleton width="70%" height="1rem" />
      </div>
    );

    const typeSkeleton = () => <Skeleton width="6rem" height="1rem" />;

    const packageSkeleton = () => <Skeleton width="5rem" height="1rem" />;

    return (
      <div className="w-full border border-gray-300 rounded-xl overflow-hidden">
        <DataTable value={skeletonRows} className="w-full" stripedRows>
          <Column
            header="Customer Name"
            headerStyle={viewType === "card" ? { display: "none" } : undefined}
            body={viewType === "card" ? mobileCardNameSkeleton : nameSkeleton}
            style={viewType === "table" ? { width: "50%" } : {}}
          />
          {viewType === "card" ? null : (
            <Column header="BC" body={typeSkeleton} />
          )}
          {viewType === "card" ? null : (
            <Column header="Total" body={packageSkeleton} />
          )}
        </DataTable>
      </div>
    );
  };

  return (
    <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300 sm:!px-6">
      <div className="w-full flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center sm:justify-between">
        <div className="flex flex-col items-start gap-1">
          <label className="flex flex-wrap items-start gap-2 text-lg font-semibold text-black leading-snug">
            <span>
              <i className="pi pi-cart-arrow-down text-green-500 text-lg mr-2"></i>
              Package Ownership List -{" "}
              <span className="font-bold italic break-words">
                {product_name}
              </span>
            </span>
          </label>
        </div>

        <div className="flex gap-2 rounded-md p-1 w-full sm:w-auto">
          {viewOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setViewType(option.value)}
              className={`px-4 py-1.5 w-full sm:w-auto rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out 
                ${
                  viewType === option.value
                    ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
                    : "text-black hover:bg-green-100 bg-slate-100"
                }`}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="col-center-start gap-2">
        <div className="w-full row-start-center gap-2">
          <i className="pi pi-box text-lg sm:text-lg text-green-500"></i>

          <AnimatePresence mode="wait">
            <span className="flex flex-row justify-start items-center gap-2 text-md sm:text-lg font-semibold text-green-500 overflow-wrap">
              {loading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Skeleton width="10rem" height="1.75rem" />
                </motion.div>
              ) : (
                <motion.div
                  key="data"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Total Packages : {formatPax(data?.total_packages) || "0"}
                </motion.div>
              )}
            </span>
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <TableSkeleton rows={10} />
          </motion.div>
        ) : viewType === "card" ? (
          <motion.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data?.customers}
              className="w-full"
              stripedRows
              selectionMode="single"
              selection={selectedCustomer}
              onSelectionChange={(e) => setSelectedCustomer(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
              paginatorTemplate={{
                layout: isMobile
                  ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                  : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              }}
            >
              <Column
                field="mobileProduct"
                header="Product Name"
                body={CustomerMobileTemplate}
                headerStyle={{ display: "none" }}
              />
            </DataTable>
          </motion.div>
        ) : (
          <motion.div
            key="table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data?.customers}
              className="w-full"
              stripedRows
              selectionMode="single"
              selection={selectedCustomer}
              onSelectionChange={(e) => setSelectedCustomer(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
              removableSort
              sortMode="multiple"
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
              paginatorTemplate={{
                layout: isMobile
                  ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                  : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              }}
            >
              <Column
                field="customer_name"
                header="Customer Name"
                body={customerBodyTemplate}
                style={{ width: "50%" }}
                sortable
              />
              <Column
                field="bc_name"
                header="BC"
                body={BCBodyTemplate}
                sortable
              />
              <Column
                field="quantity"
                header="Total"
                body={packageBodyTemplate}
                sortable
              />
            </DataTable>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RemainingPackageDetailList;
